import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MessagesSquare } from 'lucide-react';

import { MAX_OTHER_PARTICIPANTS, chatConversationsKey, createConversation } from '@/api/comms/chat.api';
import { COMMS_UNREAD_KEY } from '@/api/comms/unread.api';
import { ChatThread } from '@/components/comms/ChatThread';
import { ConversationList } from '@/components/comms/ConversationList';
// Built by Agent D (Stream E Wave 2). Props: { multi?, exclude?, onSelect }.
import { MemberPicker } from '@/components/comms/MemberPicker';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/cn';
import { eosErrorMessage } from '@/lib/eos/errors';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * Messages — `/messages` and `/messages/:conversationId` (Stream E,
 * ADR-027 §7.2). One component drives both routes: a two-pane layout on
 * desktop (conversation list + open thread) and a list-or-thread swap on
 * mobile. `?new=1` opens the new-conversation modal (CommandK deep link).
 */
export function MessagesPage() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { state: authState } = useAuth();

  const me = authState.status === 'authenticated' ? authState.me : null;
  const myMembershipId = me?.membership.id ?? null;

  const activeId = conversationId ?? null;
  const [newOpen, setNewOpen] = useState(false);

  // Consume the ?new=1 deep link once, then strip it so a refresh/back
  // doesn't reopen the modal.
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setNewOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <div className="mx-auto flex h-[calc(100vh-8rem)] min-h-[30rem] max-w-6xl flex-col">
      <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden rounded-kw-lg border border-kw-border bg-white shadow-kw-xs md:grid-cols-[320px_1fr]">
        {/* Conversation list — hidden on mobile while a thread is open. */}
        <div className={cn('min-h-0 border-kw-border md:block md:border-r', activeId ? 'hidden' : 'block')}>
          <ConversationList
            activeConversationId={activeId}
            myMembershipId={myMembershipId}
            onSelect={(id) => {
              void navigate(`/messages/${id}`);
            }}
            onNewConversation={() => {
              setNewOpen(true);
            }}
          />
        </div>

        {/* Thread — hidden on mobile until one is selected. */}
        <div className={cn('min-h-0', activeId ? 'block' : 'hidden md:block')}>
          {activeId ? (
            <ChatThread
              key={activeId}
              conversationId={activeId}
              myMembershipId={myMembershipId}
              onBack={() => {
                void navigate('/messages');
              }}
            />
          ) : (
            <EmptyThread
              onNewConversation={() => {
                setNewOpen(true);
              }}
            />
          )}
        </div>
      </div>

      <NewConversationModal
        open={newOpen}
        myMembershipId={myMembershipId}
        onClose={() => {
          setNewOpen(false);
        }}
        onCreated={(id) => {
          setNewOpen(false);
          void navigate(`/messages/${id}`);
        }}
      />
    </div>
  );
}

function EmptyThread({ onNewConversation }: { onNewConversation: () => void }) {
  return (
    <div className="hidden h-full flex-col items-center justify-center gap-3 px-6 text-center md:flex">
      <MessagesSquare className="size-10 text-kw-ghost" />
      <div>
        <p className="text-[14px] font-medium text-kw-shark">Your messages</p>
        <p className="mt-1 text-[13px] text-kw-muted">Pick a conversation on the left, or start a new one.</p>
      </div>
      <Button size="sm" variant="secondary" onClick={onNewConversation}>
        New message
      </Button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// New-conversation modal — DM (1 pick) or group (2..7 picks + optional title)
// ─────────────────────────────────────────────────────────────────────────

function NewConversationModal({
  open,
  myMembershipId,
  onClose,
  onCreated,
}: {
  open: boolean;
  myMembershipId: string | null;
  onClose: () => void;
  onCreated: (conversationId: string) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [title, setTitle] = useState('');

  // Reset the form whenever the modal opens.
  useEffect(() => {
    if (open) {
      setSelectedIds([]);
      setTitle('');
    }
  }, [open]);

  const exclude = useMemo(() => (myMembershipId ? [myMembershipId] : []), [myMembershipId]);
  const isGroup = selectedIds.length >= 2;
  const tooMany = selectedIds.length > MAX_OTHER_PARTICIPANTS;

  const mutation = useMutation({
    mutationFn: () =>
      createConversation({
        participantMembershipIds: selectedIds,
        ...(isGroup && title.trim() !== '' ? { title: title.trim() } : {}),
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: chatConversationsKey() });
      void queryClient.invalidateQueries({ queryKey: COMMS_UNREAD_KEY });
      onCreated(result.conversation.id);
    },
    onError: (err) => {
      toast.show(eosErrorMessage(err) ?? 'Could not start the conversation.', { variant: 'danger' });
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (selectedIds.length === 0 || tooMany || mutation.isPending) return;
    mutation.mutate();
  }

  return (
    <Modal open={open} onClose={onClose} title="New message" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <span className="mb-1 block text-[13px] font-medium text-kw-label">To</span>
          <MemberPicker multi exclude={exclude} onSelect={setSelectedIds} />
          <p className="mt-1 text-[12px] text-kw-muted">
            Pick one person for a direct message, or up to {MAX_OTHER_PARTICIPANTS} for a group.
          </p>
          {tooMany ? (
            <p className="mt-1 text-[12px] text-kw-danger-text">
              A group can have at most {MAX_OTHER_PARTICIPANTS} other people.
            </p>
          ) : null}
        </div>

        {isGroup ? (
          <Input
            label="Group name (optional)"
            name="conversation-title"
            value={title}
            maxLength={120}
            onChange={(e) => {
              setTitle(e.target.value);
            }}
            placeholder="e.g. Warehouse launch crew"
          />
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={mutation.isPending} disabled={selectedIds.length === 0 || tooMany}>
            {isGroup ? 'Start group' : 'Start conversation'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
